import { FilesDropZoneRef } from '@/routes/dashboard/files/FilesDropZoneWithTaskState'
import { useState, forwardRef, useImperativeHandle, useRef } from 'react'
import { StorageReference, ref as firebaseRef } from 'firebase/storage'
import { getAppStorage } from '@/firebase/storage-routing'
import { kGcsUserFileUploadsBucketRef } from '@/constants/constants-gcs'
import { getAuth } from 'firebase/auth'
import FilesDropZoneWithTaskState from '@/routes/dashboard/files/FilesDropZoneWithTaskState'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { selectAgentConversationFromId } from '@/agent/conversations/store/selectors'
import { pollForFileStatusUntilSuccess } from '@/routes/dashboard/files/utils/gcs-file-processing-poll'
import { FileUploadStatusDialogInChat } from '@/routes/dashboard/files/FileUploadStatusDialogInChat'
import { FileProcessingStatus, setFileProcessingStatus } from '@/store/slices/file-upload-tasks.slice'
import { setSelectedFilesContentExpanded } from '../store/chat-ux.slice'
import { FileOverwriteModalProvider } from '@/routes/dashboard/files/file-overwrite-confirmation/file-overwrite-modal-context'
import { RootState } from '@/store/store'
import { AgentConversationFormActions } from '@/agent/chat_form/store/slice'
import { selectAgentConversationFormTemplateFilePaths } from '@/agent/chat_form/store/selectors'

type ChatWindowDropZoneMultifileProps = {
  hideDropZoneUI?: boolean
  chatId: string
  isSingleFileLimit?: boolean
  isTemplateMode?: boolean
  onFilesAccepted?: (files: File[]) => void
}

export interface ChatWindowDropZoneMultifileRef {
  openSystemFileSelector: () => void
}

const ChatWindowDropZoneMultifile = forwardRef<ChatWindowDropZoneMultifileRef, ChatWindowDropZoneMultifileProps>(function ChatWindowDropZoneMultifile(
  props,
  ref
) {
  const { hideDropZoneUI = false, chatId: conversationId, isSingleFileLimit = false, isTemplateMode = false, onFilesAccepted } = props
  const dispatch = useAppDispatch()
  // Analytics tracking is now centralized in template-analytics middleware

  // Get the current user
  const auth = getAuth()
  const uid = auth.currentUser?.uid

  // Get template files for this conversation
  const templateFiles = useAppSelector((state: RootState) => selectAgentConversationFormTemplateFilePaths(state, { conversationId }))

  // Derive the currentFolder for uploads
  const matterId = useAppSelector((state: RootState) => selectAgentConversationFromId(state, { conversationId })?.matter_id ?? null)
  const storage = getAppStorage({ bucketUrl: kGcsUserFileUploadsBucketRef, legacy_always_cloud: true })
  const uploadFolderPath = matterId && uid ? `users/${uid}/${matterId}/Assistant Uploads` : null
  const currentFolderRef: StorageReference | null = uploadFolderPath ? firebaseRef(storage, uploadFolderPath) : null

  // Local state
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false)

  // Expose method for opening the computer file selector from FilesDropZone
  const filesDropZoneRef = useRef<FilesDropZoneRef | null>(null)

  // Helper function to clear existing template files
  const clearTemplateFiles = () => {
    if (templateFiles.length > 0) {
      dispatch(AgentConversationFormActions.clearTemplateFiles({ conversationId }))
    }
  }

  useImperativeHandle(ref, () => ({
    openSystemFileSelector: () => {
      // Call the file selector
      filesDropZoneRef.current?.openSystemFileSelector()
    },
  }))

  const handleTaskListItemUpdated = () => {
    setStatusDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setStatusDialogOpen(false)
  }

  return (
    <div className="px-4">
      <FileOverwriteModalProvider>
        {currentFolderRef && (
          <FilesDropZoneWithTaskState
            ref={filesDropZoneRef}
            onTaskListItemUpdated={handleTaskListItemUpdated}
            currentFolder={currentFolderRef}
            singleFileLimit={isSingleFileLimit}
            hideDropZoneUI={hideDropZoneUI}
            chatId={conversationId}
            gcsConfirmOverwrite={true}
            onFilesAccepted={onFilesAccepted}
            // When the user indicates they want to use an existing file
            onUseExistingFileOption={(item) => {
              const sourcePath = item.sourcePath

              // Set as selected file for this chat
              if (isTemplateMode) {
                // Clear existing template files before adding new one
                clearTemplateFiles()

                dispatch(AgentConversationFormActions.addTemplateFile({ conversationId, filePath: sourcePath }))
              } else {
                dispatch(AgentConversationFormActions.addFile({ conversationId, filePath: sourcePath }))
              }

              // Expand the selected files content
              dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))
            }}
            // When it is a new file or the user indicates they want to overwrite the existing file
            onNewTaskCreated={(item) => {
              const sourcePath = item.sourcePath

              // Set as selected file for this chat
              if (isTemplateMode) {
                // Clear existing template files before adding new one
                clearTemplateFiles()

                dispatch(AgentConversationFormActions.addTemplateFile({ conversationId, filePath: sourcePath }))
              } else {
                dispatch(AgentConversationFormActions.addFile({ conversationId, filePath: sourcePath }))
              }

              // Add to processing state as pending (dispatch after template file is registered so middleware can link it)
              dispatch(setFileProcessingStatus({ sourcePath, status: FileProcessingStatus.PENDING }))

              // Expand the selected files content
              dispatch(setSelectedFilesContentExpanded({ conversationId, expanded: true }))

              // Upload start tracking handled by middleware
            }}
            onTaskListItemCompleted={(item) => {
              // Start the polling task for this file
              pollForFileStatusUntilSuccess(item.sourcePath, () => {
                /* Completion tracking handled by middleware */
              })
            }}
          />
        )}
      </FileOverwriteModalProvider>
      <FileUploadStatusDialogInChat open={statusDialogOpen} onClose={handleCloseDialog} conversationId={conversationId} />
    </div>
  )
})

export default ChatWindowDropZoneMultifile
